import type { DataReadinessResponse } from '@services/onboardingApi'
import './DataReadinessCard.css'

interface DataReadinessCardProps {
  readiness: DataReadinessResponse | null
  loading: boolean
}

const TIER_INFO: Record<string, { label: string; color: string; description: string }> = {
  identity:  { label: 'Identity',  color: '#6b7280', description: 'Company profile only. Upload data to unlock deeper analysis.' },
  sparse:    { label: 'Sparse',    color: '#f59e0b', description: 'Limited data. A few vitals are computable.' },
  partial:   { label: 'Partial',   color: '#3b82f6', description: 'Good foundation. Most strategic metrics available.' },
  complete:  { label: 'Complete',  color: '#10b981', description: 'Full analysis power. Historical trends available.' },
  rich:      { label: 'Rich',      color: '#8b5cf6', description: 'All data domains populated. Maximum insight depth.' },
}

const DOMAIN_LABELS: Record<string, string> = {
  sales:            'Sales',
  customers:        'Customers',
  accounting:       'Accounting',
  products:         'Products',
  employees:        'Employees',
  competitors:      'Competitors',
  suppliers:        'Suppliers',
  pipeline:         'Sales Pipeline',
  interactions:     'Interactions',
  reviews:          'Reviews',
  chart_of_accounts: 'Chart of Accounts',
}

function DataReadinessCard({ readiness, loading }: DataReadinessCardProps) {
  if (loading) {
    return <div className="readiness-card readiness-loading">Checking data readiness...</div>
  }

  if (!readiness) {
    return null
  }

  const tier = TIER_INFO[readiness.tier] || TIER_INFO.identity

  return (
    <div className="readiness-card">
      <div className="readiness-header">
        <div className="readiness-tier-badge" style={{ backgroundColor: tier.color }}>
          {tier.label}
        </div>
        <div className="readiness-summary">
          <span>{readiness.vitals_count} vitals computable</span>
          <span className="readiness-dot" />
          <span>{readiness.populated_domains} domains populated</span>
        </div>
      </div>

      <p className="readiness-description">{tier.description}</p>

      <div className="readiness-domains">
        {Object.entries(readiness.domain_counts).map(([domain, count]) => (
          <div key={domain} className="readiness-domain">
            <span className="domain-name">{DOMAIN_LABELS[domain] || domain}</span>
            <span className={`domain-count ${count > 0 ? 'populated' : 'empty'}`}>
              {count > 0 ? `${count} records` : 'No data'}
            </span>
          </div>
        ))}
      </div>

      {readiness.ivp_status !== 'none' && (
        <div className="readiness-ivp">
          IVP: <span className={`ivp-status ivp-${readiness.ivp_status}`}>{readiness.ivp_status}</span>
        </div>
      )}
    </div>
  )
}

export default DataReadinessCard
